import React, { useEffect, useState } from 'react';
import {Button, Form,
        FormGroup,
        Label,
        Input,
        ButtonGroup,
    } from 'reactstrap';
import {useDispatch, useSelector} from 'react-redux';
import {roles} from '../../components/RoleBadge';
import {fetchOrganizations} from '../../slices/organizations.slice';
import {checkUsernameAvailability, resetUsernameCheck} from '../../slices/user.slice';
import BadgeSelect from '../../components/BadgeSelect';
import PasswordInput from '../../components/PasswordInput';
import useSavePrompt from '../../utils/useSavePrompt';
import SavePrompt from '../../components/SavePrompt'
import {defaultContentRatingList} from '../../utils/contentRatings';

const UserForm = ({ user, isEditing = false, onSubmit, parentOrganization }) => {
    const dispatch = useDispatch();
    const token = useSelector(state => state.auth.token);
    const auth = useSelector((state) => state.auth);
    const organizations = useSelector(state => state.organizations.list);
    const organizationsStatus = useSelector((state) => state.organizations.status);
    const usernameAvailable = useSelector(state => state.users.usernameAvailable);
    const usernameCheckStatus = useSelector(state => state.users.usernameCheckStatus);
    const [originalData, setOriginaldata] = useState(user)

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        password: isEditing ? undefined : '',
        role: 'user',
        parentOrganization,
        profilePic: '',
        allowedContentRestrictions: [],
    });

    const errors = {};
    if (auth?.createUserErrors?.length) {
        auth.createUserErrors.forEach(({ field, message }) => errors[field] = message);
    }

    useEffect(() => {
        if (isEditing && user) {
            setFormData({
                ...user,
                parentOrganization: user.parentOrganization ? user.parentOrganization._id : '',
            });
            setOriginaldata({
                ...user,
                parentOrganization: user.parentOrganization ? user.parentOrganization._id : '',
            });
        }
    }, [user, isEditing]);

    useEffect(() => {
        if (user && ['root', 'bac-user', 'bac-admin'].includes(user.role)) {
            dispatch(fetchOrganizations(token));
        }
    }, [token, dispatch, user]);

    useEffect(() => {
        // Reset username check when component unmounts or user changes
        return () => {
            dispatch(resetUsernameCheck());
        };
    }, [dispatch]);

    useEffect(() => {
        // Debounce username check
        if (!formData.username) {
            dispatch(resetUsernameCheck());
            return;
        }

        // Skip check if editing and username hasn't changed
        if (isEditing && user && originalData && formData.username === originalData.username) {
            dispatch(resetUsernameCheck());
            return;
        }

        const timeoutId = setTimeout(() => {
            dispatch(checkUsernameAvailability({
                username: formData.username,
                userId: isEditing && user ? user._id : null
            }));
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [formData.username, dispatch, isEditing, user, originalData]);

    const { blocker, isDirty } = useSavePrompt(originalData, formData)

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleRoleChange = (role) => {
        setFormData(prev => ({
            ...prev,
            role,
            parentOrganization: ['root','bac-admin','bac-user'].includes(role)
                ? ''
                : prev.parentOrganization
        }))
    }

    const handleParentOrgChange = e => {
        const { value } = e.target
        setFormData(prev => ({
            ...prev,
            parentOrganization: value,
            role: value ? 'site-admin' : prev.role
        }))
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let result = {...formData};
        if (result.parentOrganization === '') {
            result.parentOrganization = null;
        }
        onSubmit(result);
    };

    const shouldDisplayRole = (role) => {
        if (formData.parentOrganization !== "") {
            return !role.startsWith('site-');
        } else {
            return role.startsWith('site-');
        }
    }

    const getRoleRank = (role) => roles.find(r => r.role === role)?.rank;

    const handleContentRating = (value) => {
        const index = defaultContentRatingList.indexOf(value);
        if (index !== -1) {
            const newValues = defaultContentRatingList.slice(0, index + 1);
            setFormData({
                ...formData,
                allowedContentRestrictions: newValues,
            });
        }
    };

    return user && (
        <Form onSubmit={handleSubmit}>
            <div className="border-start border-primary p-3 bg-broken">
                <div className="d-flex gap-3">
                    <FormGroup className="flex-grow-1">
                        <Label for="firstName">First Name</Label>
                        <Input
                            type="text"
                            name="firstName"
                            id="firstName"
                            placeholder="First Name"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                        />
                    </FormGroup>

                    <FormGroup className="flex-grow-1">
                        <Label for="lastName">Last Name</Label>
                        <Input
                            type="text"
                            name="lastName"
                            id="lastName"
                            placeholder="Last Name"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                        />
                    </FormGroup>
                </div>

                <div className="flex gap-3">
                    <FormGroup>
                        <Label for="username">Username</Label>
                        <div className="position-relative">
                            <Input
                                type="text"
                                name="username"
                                id="username"
                                invalid={!!errors.username || (usernameCheckStatus === 'success' && !usernameAvailable)}
                                valid={usernameCheckStatus === 'success' && usernameAvailable}
                                placeholder="Username"
                                value={formData.username}
                                onChange={handleChange}
                                required
                            />
                            {usernameCheckStatus === 'loading' && formData.username && (
                                <span className="position-absolute end-0 top-50 translate-middle-y pe-3">
                                    <span className="fas fa-spinner fa-spin text-muted" />
                                </span>
                            )}
                        </div>
                        {errors.username && <div className="invalid-feedback d-block">{errors.username}</div>}
                        {!errors.username && usernameCheckStatus === 'success' && !usernameAvailable && (
                            <div className="invalid-feedback d-block">
                                This username is already taken
                            </div>
                        )}
                        {!errors.username && usernameCheckStatus === 'success' && usernameAvailable && (
                            <div className="valid-feedback d-block">
                                Username is available
                            </div>
                        )}
                    </FormGroup>

                    <FormGroup className="flex-grow-1">
                        <Label for="email">Email (optional)</Label>
                        <Input
                            type="email"
                            name="email"
                            id="email"
                            invalid={!!errors.email}
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </FormGroup>

                    {!isEditing && (
                        <FormGroup>
                            <Label for="password">Password</Label>

                            {(!formData.email || formData.email === "") ? (
                                <PasswordInput
                                    name="password"
                                    id="password"
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                            ) : (
                                <p>
                                    <span className="fas fa-info-circle text-primary me-2" />
                                    An email will be sent with a link to set the password
                                </p>
                            )}
                        </FormGroup>
                    )}
                </div>
            </div>

            <div className="border-start border-primary p-3 bg-broken mt-3">
                {['root','bac-admin','bac-user'].includes(auth.user.role) && (
                    <FormGroup>
                        <Label for="parentOrganization">Parent Organization</Label>
                        <Input
                            type="select"
                            name="parentOrganization"
                            id="parentOrganization"
                            className="text-primary"
                            value={formData.parentOrganization}
                            onChange={handleParentOrgChange}
                            invalid={formData.role.startsWith('site-') && !formData.parentOrganization}
                        >
                            <option value="">Select an Organization</option>
                            {organizationsStatus === 'success' &&
                                organizations.map(org => (
                                    <option key={org._id} value={org._id}>
                                        {org.name}
                                    </option>
                                ))
                            }
                        </Input>
                        {formData.role.startsWith('site-') && !formData.parentOrganization && (
                            <div className="invalid-feedback">
                                A parent organization is required for site roles.
                            </div>
                        )}
                    </FormGroup>
                )}

                <FormGroup>
                    <Label className="d-block">Role</Label>
                    <ButtonGroup className="d-flex flex-wrap justify-content-between">
                        {roles.filter(r => getRoleRank(r.role) >= getRoleRank(user.role)).map(({role, label, color, icon}) => (
                            <Button
                                key={role}
                                className="m-2 col-md-3 py-3"
                                outline={formData.role !== role}
                                color={shouldDisplayRole(role) ? 'secondary' : color}
                                disabled={shouldDisplayRole(role)}
                                onClick={() => handleRoleChange(role)}
                            >
                                <span className={`fas me-2 ${icon}`} /> {label}
                            </Button>
                        ))}
                    </ButtonGroup>
                </FormGroup>
            </div>

            {['root', 'bac-admin', 'bac-user', 'site-admin'].includes(auth.user.role) && formData.parentOrganization && (
                <div className="border-start border-primary p-3 bg-broken mt-3">
                    <div className="d-flex gap-3">
                        <FormGroup>
                            <Label>Allowed content ratings</Label>
                            <BadgeSelect
                                defaultList={defaultContentRatingList}
                                onChange={handleContentRating}
                                values={formData.allowedContentRestrictions}
                                multiple
                                append="+"
                                contentRestrictions
                            />
                        </FormGroup>
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-end my-3">
                <Button
                    color="primary"
                    type="submit"
                    disabled={
                        (formData.role.startsWith('site-') && !formData.parentOrganization) ||
                        (usernameCheckStatus === 'success' && !usernameAvailable) ||
                        usernameCheckStatus === 'loading'
                    }
                >
                    {isDirty && <span className="unsaved-dot" />}
                    <span className={`fas fa-user-${isEditing ? 'check' : 'plus'} me-2`} />
                    {isEditing ? 'Update user' : 'Create user'}
                </Button>
            </div>
            <SavePrompt blocker={blocker}/>
        </Form>
    );
};

export default UserForm;
